import { Navigate, useLocation } from 'react-router';
import { getBookingDraft, useBookingDraft } from './booking-draft';

/**
 * Address presence + valid coordinates. Every booking has to carry
 * lat/lng to the backend (the pro matcher + travel-radius filter +
 * subscription cycle re-spawning all read them), so an address_line1
 * alone isn't enough — we also require finite coordinates inside the
 * legal WGS-84 range. Free-typed strings that never resolved through
 * Google Places fail this check and the guard bounces back to
 * /booking-location.
 */
function hasGeocodedAddress(
  addr:
    | {
        address_line1?: string | null;
        latitude?: number | null;
        longitude?: number | null;
      }
    | null
    | undefined,
): boolean {
  if (!addr?.address_line1) return false;
  const lat = Number(addr.latitude);
  const lng = Number(addr.longitude);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/**
 * Guard for routes that assume the customer has already picked an
 * address — /booking, /select-treatment, /select-professional, etc.
 * The wizard reads `draft.address.{latitude,longitude}` for the pro
 * radius filter, and `address_line1` becomes the booking's pickup
 * address at checkout. Without those fields these screens are dead
 * ends, so we bounce back to /booking-location.
 *
 * Re-subscribes via `useBookingDraft` so a fresh address selected
 * elsewhere (e.g. tab restored from a draft) unblocks the route
 * without a hard refresh.
 */
export function RequireBookingAddress({
  children,
}: {
  children: React.ReactNode;
}) {
  const draft = useBookingDraft();
  const location = useLocation();
  if (!hasGeocodedAddress(draft?.address)) {
    return (
      <Navigate
        to="/booking-location"
        replace
        state={{ from: location.pathname }}
      />
    );
  }
  return <>{children}</>;
}

/**
 * Imperative variant — useful inside event handlers or effects that
 * want to short-circuit when no geocoded address is set without
 * rendering a Navigate. Reads localStorage directly; not reactive.
 */
export function hasBookingAddress(): boolean {
  return hasGeocodedAddress(getBookingDraft()?.address);
}

